// src/components/AdminPanel/Dashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../../firebase";

const normalise = (raw) => {
  const s = (raw || "").toLowerCase().trim();
  if (s === "accepted" || s === "selected") return "accepted";
  if (s === "rejected") return "rejected";
  if (s === "blocked") return "blocked";
  return "pending";
};

const Dashboard = () => {
  const [jobApps, setJobApps] = useState([]);
  const [resApps, setResApps] = useState([]);
  const [certificationsCount, setCertificationsCount] = useState(0);

  // ── Listeners ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubJobs = onSnapshot(query(collection(db, "jobApplications")), (snap) => {
      setJobApps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubRes = onSnapshot(query(collection(db, "opportunityApplications")), (snap) => {
      setResApps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubCerts = onSnapshot(collection(db, "certifications"), (snap) => {
      setCertificationsCount(snap.size);
    });

    return () => { unsubJobs(); unsubRes(); unsubCerts(); };
  }, []);

  // ── Derived counts ───────────────────────────────────────────────────────
  const getCounts = (apps) => {
    const result = { pending: 0, accepted: 0, rejected: 0, blocked: 0 };
    apps.forEach((app) => {
      const n = normalise(app.status);
      if (result[n] !== undefined) result[n]++;
    });
    return result;
  };

  const jobCounts = useMemo(() => getCounts(jobApps), [jobApps]);
  const resCounts = useMemo(() => getCounts(resApps), [resApps]);

  const totalCandidates = jobApps.length;
  const totalReservations = resApps.length;
  const totalActiveReviews = jobCounts.pending + resCounts.pending;

  // ── Theme ────────────────────────────────────────────────────────────────
  const cardGradient = "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)";
  const highlightedGradient = "linear-gradient(135deg, #FF9F2E 0%, #FF8C00 100%)";

  return (
    <div className="dashboard-page">
      <h1 className="page-heading">
        <i className="fas fa-home" style={{ color: "#FF8C00" }}></i> System Dashboard
      </h1>

      {/* ── Top Level System Metrics ─────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "30px", flexWrap: "wrap" }}>

        {/* Total Job Applications */}
        <div style={{ background: highlightedGradient, padding: "24px", borderRadius: "16px", color: "white", minWidth: "160px", flex: 1, boxShadow: "0 8px 20px rgba(255,140,0,0.3)", border: "1px solid rgba(255,255,255,0.2)", transform: "translateY(-2px)", transition: "all 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-briefcase" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Total Jobs Apps</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>{totalCandidates}</p>
        </div>

        {/* Total Reservations */}
        <div style={{ background: highlightedGradient, padding: "24px", borderRadius: "16px", color: "white", minWidth: "160px", flex: 1, boxShadow: "0 8px 20px rgba(255,140,0,0.3)", border: "1px solid rgba(255,255,255,0.2)", transform: "translateY(-2px)", transition: "all 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-calendar-check" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Total Reservations</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>{totalReservations}</p>
        </div>

        {/* Pending Reviews (System Wide) */}
        <div style={{ background: cardGradient, padding: "24px", borderRadius: "16px", color: "white", minWidth: "160px", flex: 1, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid rgba(255,255,255,0.15)", transition: "all 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-clock" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Global Pending</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>{totalActiveReviews}</p>
        </div>

        {/* Certifications */}
        <div style={{ background: cardGradient, padding: "24px", borderRadius: "16px", color: "white", minWidth: "160px", flex: 1, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid rgba(255,255,255,0.15)", transition: "all 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <i className="fas fa-certificate" style={{ fontSize: "28px", opacity: 0.9 }}></i>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>Total Certifications</h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>{certificationsCount}</p>
        </div>
      </div>

      {/* ── Two-Column Detailed Breakdown ─────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>

        {/* Job Applications Summary Card */}
        <div style={{ background: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #E2E8F0" }}>
          <h2 style={{ fontSize: "18px", color: "#2D3748", margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fas fa-briefcase" style={{ color: "#FF8C00" }}></i> Job Applications
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid #EDF2F7" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Accepted</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{jobCounts.accepted}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid #EDF2F7" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Rejected</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{jobCounts.rejected}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Blocked</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{jobCounts.blocked}</span>
            </div>
          </div>
        </div>

        {/* Reservation Applications Summary Card */}
        <div style={{ background: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #E2E8F0" }}>
          <h2 style={{ fontSize: "18px", color: "#2D3748", margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fas fa-calendar-check" style={{ color: "#FF8C00" }}></i> Reservation Applications
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid #EDF2F7" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Accepted</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{resCounts.accepted}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid #EDF2F7" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Rejected</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{resCounts.rejected}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#718096", fontSize: "14px", fontWeight: "500" }}>Blocked</span>
              <span style={{ background: "#ff9f2e", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold" }}>{resCounts.blocked}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;